import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { UserActivityDataPoint } from '../hooks/useUserActivity';
import { buildUserActivityChartData } from '../utils/userActivityChart';

interface UserActivityChartProps {
  title: string;
  subtitle?: string;
  data: UserActivityDataPoint[];
  displayNames: Record<string, string>;
  height?: number;
}

const UserActivityChart: React.FC<UserActivityChartProps> = ({
  title,
  subtitle,
  data,
  displayNames,
  height = 300,
}) => {
  const { chartData, seriesKeys, seriesLabels, colors } = React.useMemo(
    () => buildUserActivityChartData(data, displayNames),
    [data, displayNames]
  );

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {chartData.length > 0 && seriesKeys.length > 0 ? (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <RechartsTooltip
                formatter={(value: number, name: string) => [
                  value,
                  seriesLabels[name] ?? name,
                ]}
              />
              <Legend
                formatter={(value: string) => seriesLabels[value] ?? value}
              />
              {seriesKeys.map(key => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="activity"
                  fill={colors[key]}
                  name={key}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="textSecondary">No activity recorded</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default UserActivityChart;
